import { isQualityWarningList, qualityWarningText } from "./html5-quality-warnings";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Html5Project } from "@interactads/creative-model";
import {Button} from "@/ui/button";
import { api, call } from "@/api/client";
import { ensureHtml5FramePolicy } from "../html5-frame-policy";
import { StudioContext } from "./context";
type Grant = { url: string; origin: string; manifestSha256: string; expiresAt: string };

export function validHtml5PreviewGrant(grant: Grant, manifest: string, studioOrigin: string, now = Date.now()) {
  try {
    const url = new URL(grant.url);
    const expires = Date.parse(grant.expiresAt);
    return (
      grant.manifestSha256 === manifest &&
      url.origin === grant.origin &&
      url.origin !== studioOrigin &&
      !url.username &&
      !url.password &&
      !url.hash &&
      Number.isFinite(expires) &&
      expires > now &&
      expires - now <= 10 * 60 * 1000 &&
      (url.protocol === "https:" || (url.protocol === "http:" && ["127.0.0.1", "[::1]"].includes(url.hostname)))
    );
  } catch {
    return false;
  }
}
/** No srcdoc/blob/eval or same-origin code. A document cannot grant itself preview authority. */
export function IsolatedHtml5Preview({
  project,
  scale,
  fallback,
}: {
  project: Html5Project;
  scale: number;
  fallback: ReactNode;
}) {
  const context = useContext(StudioContext);
  const frame=useRef<HTMLIFrameElement>(null),reported=useRef('');
  const [quality,setQuality]=useState('');
  const [needsPlaytest,setNeedsPlaytest]=useState(false),[confirming,setConfirming]=useState(false);
  const smokeReport=useRef<{manifestSha256:string;findings:string[];started:boolean;checkedImages:number;warnings?:string[]}|null>(null);
  const [warnings,setWarnings]=useState<string[]>([]);
  const warningsPosted=useRef<string|null>(null);
  const [grant, setGrant] = useState<Grant | null>(null);
  const orgId = context?.orgId,
    versionId = context?.versionId,
    hash = project.manifestSha256;
  useEffect(() => {
    let cancelled = false;
    let expiry: ReturnType<typeof setTimeout> | undefined;
    setGrant(null);setQuality('');setNeedsPlaytest(false);smokeReport.current=null;reported.current='';setWarnings([]);warningsPosted.current=null;
    if (!orgId || !versionId || !hash) return;
    void call(() =>
      api.GET("/orgs/{orgId}/versions/{versionId}/html5-preview", { params: { path: { orgId, versionId } } })
    )
      .then((result) => {
        if (
          cancelled ||
          !validHtml5PreviewGrant(result, hash, window.location.origin) ||
          !ensureHtml5FramePolicy(result.origin)
        )
          return;
        setGrant(result);
        expiry = setTimeout(() => setGrant(null), Math.max(0, Date.parse(result.expiresAt) - Date.now()));
      })
      .catch(() => undefined); // Preserve the explicit blocked preview until host qualification.
    return () => {
      cancelled = true;
      if (expiry) clearTimeout(expiry);
    };
  }, [orgId, versionId, hash]);
  useEffect(()=>{
    if(!grant||!orgId||!versionId)return;
    const receive=(event:MessageEvent)=>{const d=event.data;if(event.source!==frame.current?.contentWindow||d?.type!=='interactads:quality')return;
      if(!Array.isArray(d.findings)||d.findings.length>30||d.findings.some((x:unknown)=>typeof x!=='string'||x.length>100)||typeof d.started!=='boolean'||!Number.isInteger(d.checkedImages)||d.checkedImages<0||d.checkedImages>200)return;
      if(isQualityWarningList(d.warnings)){setWarnings(d.warnings);if(smokeReport.current)smokeReport.current={...smokeReport.current,warnings:d.warnings};
        // Only informs the record: a warning never changes the verdict or starts anything paid.
        if(d.warningsFinal===true&&warningsPosted.current!==grant.manifestSha256){warningsPosted.current=grant.manifestSha256;void call(()=>api.POST('/orgs/{orgId}/versions/{versionId}/html5-quality',{params:{path:{orgId,versionId}},body:{manifestSha256:grant.manifestSha256,findings:d.findings,warnings:d.warnings,started:d.started,checkedImages:d.checkedImages}})).catch(()=>undefined);}}
      const good=d.findings.length===0&&d.started;const verdict=grant.manifestSha256+':'+(good?'pass':'fail');if(reported.current===verdict||reported.current===grant.manifestSha256+':fail')return;reported.current=verdict;setNeedsPlaytest(good);smokeReport.current={manifestSha256:grant.manifestSha256,findings:d.findings,started:d.started,checkedImages:d.checkedImages};setQuality(good?'La comprobación inicial pasó. Prueba la experiencia completa antes de aprobarla.':'El anuncio tiene errores de ejecución, recursos o composición. No está terminado.');
      void call(()=>api.POST('/orgs/{orgId}/versions/{versionId}/html5-quality',{params:{path:{orgId,versionId}},body:{manifestSha256:grant.manifestSha256,findings:d.findings,started:d.started,checkedImages:d.checkedImages}})).then(result=>{if(result.playtested){setNeedsPlaytest(false);setQuality('Prueba de interacción confirmada.');}}).catch(()=>{setNeedsPlaytest(false);setQuality('No pudimos registrar la comprobación. El anuncio sigue pendiente de validación.');});
    };window.addEventListener('message',receive);return()=>window.removeEventListener('message',receive);
  },[grant,orgId,versionId]);
  if (!grant || !validHtml5PreviewGrant(grant, hash, window.location.origin)) return fallback;
  return (
    <div>
      {quality?<p role="status" className="text-xs">{quality}</p>:null}
      {warnings.length?<ul aria-label="Advertencias de composición" className="grid gap-1 text-xs text-warning">{warnings.map(w=><li key={w}>{qualityWarningText(w)}</li>)}</ul>:null}
      {needsPlaytest&&orgId&&versionId?<Button disabled={confirming} onClick={async()=>{const checked=smokeReport.current;if(!checked)return;setConfirming(true);try{await call(()=>api.POST('/orgs/{orgId}/versions/{versionId}/html5-quality',{params:{path:{orgId,versionId}},body:{...checked,playtested:true}}));setNeedsPlaytest(false);setQuality('Prueba de interacción confirmada.');}catch{setQuality('No se pudo confirmar la prueba. Conservamos el anuncio pendiente de validación.');}finally{setConfirming(false);}}}>Confirmar que probé la experiencia completa</Button>:null}
      <div style={{ width: project.width * scale, height: project.height * scale, overflow: "hidden" }}>
      <iframe
        ref={frame}
        title="Vista previa HTML5 aislada"
        src={grant.url}
        sandbox="allow-scripts"
        allow=""
        referrerPolicy="no-referrer"
        style={{
          width: project.width,
          height: project.height,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      />
      </div>
    </div>
  );
}
